/**
 * Help content admin: the page editor (title, route, menu path, access note, sort order, active,
 * summary, suggested questions and the help per tab). Saves with PUT api/help/admin/pages/{pageKey};
 * the tabs sent replace the page's tabs.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IconAlertCircle, IconDeviceFloppy, IconArrowBackUp, IconTrash, IconWand } from '@tabler/icons-react';
import { Alert, Badge, Button, Card, Group, NumberInput, SimpleGrid, Stack, Switch, Text, TextInput, Title } from '@/components/ui';
import { ConfirmDialog } from '@/components/advanced';
import { toast } from '@/components/ui/providers/AlertProvider';
import { HelpLimits, useHelpService, type HelpPageView } from '@actschurch/ccms-sdk';
import { helpKeys, type HelpPageDef } from '@/components/help';
import { HelpHtmlField } from './HelpHtmlField';
import { QuestionsInput } from './QuestionsInput';
import { errorText, formToSaveRequest, pageFormSignature, pageToForm, type PageForm, type TabForm } from './helpAdminUtils';

interface HelpPageEditorProps {
  pageKey: string;
  page: HelpPageView | null;
  def: HelpPageDef | undefined;
  onSaved: (page: HelpPageView) => void;
  onDeleted: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export function HelpPageEditor({ pageKey, page, def, onSaved, onDeleted, onDirtyChange }: HelpPageEditorProps) {
  const help = useHelpService();
  const queryClient = useQueryClient();
  // Re-read the stored page only when it was saved again (not when the list refetches).
  const pageStamp = `${page ? 'stored' : 'none'}|${page?.updatedAt ?? ''}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initial = useMemo(() => pageToForm(pageKey, page, def), [pageKey, pageStamp, def]);
  const [form, setForm] = useState<PageForm>(initial);
  const [baseline, setBaseline] = useState<PageForm>(initial);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setForm(initial);
    setBaseline(initial);
    setSaveError(null);
  }, [initial]);

  const dirty = useMemo(() => pageFormSignature(form) !== pageFormSignature(baseline), [form, baseline]);
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);

  const set = (patch: Partial<PageForm>) => setForm((f) => ({ ...f, ...patch }));
  const setTab = (tabKey: string, patch: Partial<TabForm>, absorb = false) => {
    const apply = (f: PageForm) => ({ ...f, tabs: f.tabs.map((t) => (t.tabKey === tabKey ? { ...t, ...patch } : t)) });
    setForm(apply);
    if (absorb) setBaseline(apply);
  };

  const titleError = form.title.trim() ? null : 'Give the page a title.';
  const appError = form.appKey.trim() ? null : 'The app key is missing.';

  const save = useMutation({
    mutationFn: () => help.saveHelpPage(pageKey, formToSaveRequest(form)),
    onSuccess: (saved) => {
      setSaveError(null);
      const next = pageToForm(pageKey, saved, def);
      setForm(next);
      setBaseline(next);
      queryClient.invalidateQueries({ queryKey: helpKeys.page(pageKey) });
      toast.success('Help saved', saved.title || pageKey);
      onSaved(saved);
    },
    onError: (err) => {
      const msg = errorText(err, 'The help could not be saved. Try again.');
      setSaveError(msg);
      toast.error('Could not save the help', msg);
    },
  });

  const remove = useMutation({
    mutationFn: () => help.deleteHelpPage(pageKey),
    onSuccess: () => {
      setConfirmDelete(false);
      queryClient.invalidateQueries({ queryKey: helpKeys.page(pageKey) });
      toast.success('Help removed', 'People see "not written yet" on this page again.');
      onDeleted();
    },
    onError: (err) => toast.error('Could not remove the help', errorText(err, 'Try again.')),
  });

  const fillFromRegistry = () => {
    if (!def) return;
    set({ title: def.title, route: def.route, menuPath: def.menuPath, accessNote: def.accessNote, appKey: def.appKey, sortOrder: def.sortOrder });
  };

  const canSave = dirty && !titleError && !appError && !save.isPending;

  return (
    <Stack gap="lg">
      <Card withBorder radius="md" padding="lg">
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Stack gap={0}>
              <Title order={4}>Page</Title>
              <Text size="xs" c="dimmed">
                Ask AI uses the route, menu path and access note to send people to the right page.
              </Text>
            </Stack>
            {def && (
              <Button variant="subtle" size="compact-sm" leftSection={<IconWand size={14} />} onClick={fillFromRegistry}>
                Fill from the app
              </Button>
            )}
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label="Title" value={form.title} onChange={(e) => set({ title: e.currentTarget.value })} error={titleError} required maxLength={200} />
            <TextInput label="App key" value={form.appKey} onChange={(e) => set({ appKey: e.currentTarget.value })} error={appError} disabled={!!def} description={def ? 'Set by the app.' : undefined} />
            <TextInput
              label="Route"
              placeholder="/app/events/sources"
              description="Use :id for a detail page, for example /app/events/events/:id."
              value={form.route}
              onChange={(e) => set({ route: e.currentTarget.value })}
              maxLength={300}
            />
            <TextInput label="Menu path" placeholder="Events › Planning Center › Sources" value={form.menuPath} onChange={(e) => set({ menuPath: e.currentTarget.value })} maxLength={300} />
            <TextInput label="Who can open it" placeholder="Events admins" value={form.accessNote} onChange={(e) => set({ accessNote: e.currentTarget.value })} maxLength={300} />
            <NumberInput label="Sort order" value={form.sortOrder} onChange={(v) => set({ sortOrder: typeof v === 'number' ? v : Number(v) || 0 })} min={0} max={9999} allowDecimal={false} />
          </SimpleGrid>
          <Switch label="Active" description="Inactive help is hidden from people but kept here." checked={form.isActive} onChange={(e) => set({ isActive: e.currentTarget.checked })} />
          {def?.purpose && (
            <Text size="xs" c="dimmed">
              What the page is for: {def.purpose}
            </Text>
          )}
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="lg">
        <Stack gap="md">
          <HelpHtmlField
            label="Summary"
            description="What the page is for and how to set it up. Use short paragraphs and lists. Headings 3 and 4 are kept."
            value={form.summaryHtml}
            onChange={(html, absorb) => {
              setForm((f) => ({ ...f, summaryHtml: html }));
              if (absorb) setBaseline((b) => ({ ...b, summaryHtml: html }));
            }}
            minHeight={180}
          />
          <QuestionsInput label="Suggested questions for this page" value={form.suggestedQuestions} onChange={(v) => set({ suggestedQuestions: v })} max={HelpLimits.maxPageSuggestedQuestions} />
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="lg">
        <Stack gap="md">
          <Stack gap={0}>
            <Title order={4}>Tabs</Title>
            <Text size="xs" c="dimmed">
              Shown behind the ? inside each tab and in the help panel.
            </Text>
          </Stack>
          {form.tabs.length === 0 ? (
            <Text size="sm" c="dimmed">
              This page has no tabs.
            </Text>
          ) : (
            form.tabs.map((t) => (
              <Card key={t.tabKey} withBorder radius="md" padding="md">
                <Stack gap="sm">
                  <Group gap="xs" wrap="wrap">
                    <Badge variant="light" styles={{ root: { textTransform: 'none' } }}>
                      {t.tabKey}
                    </Badge>
                    {!t.inRegistry && (
                      <Badge variant="light" color="orange" styles={{ root: { textTransform: 'none' } }}>
                        Not on the page any more
                      </Badge>
                    )}
                    {t.purpose && (
                      <Text size="xs" c="dimmed">
                        {t.purpose}
                      </Text>
                    )}
                  </Group>
                  <TextInput label="Tab title" value={t.title} onChange={(e) => setTab(t.tabKey, { title: e.currentTarget.value })} maxLength={120} />
                  <HelpHtmlField
                    label="What this tab does"
                    value={t.bodyHtml}
                    onChange={(html, absorb) => setTab(t.tabKey, { bodyHtml: html }, absorb)}
                    minHeight={110}
                  />
                  <QuestionsInput
                    label="Suggested questions for this tab"
                    value={t.suggestedQuestions}
                    onChange={(v) => setTab(t.tabKey, { suggestedQuestions: v })}
                    max={HelpLimits.maxTabSuggestedQuestions}
                  />
                  {!t.inRegistry && (
                    <Group>
                      <Button
                        variant="subtle"
                        color="red"
                        size="compact-sm"
                        leftSection={<IconTrash size={14} />}
                        onClick={() => setForm((f) => ({ ...f, tabs: f.tabs.filter((x) => x.tabKey !== t.tabKey) }))}
                      >
                        Remove this tab
                      </Button>
                    </Group>
                  )}
                </Stack>
              </Card>
            ))
          )}
        </Stack>
      </Card>

      {saveError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} title="Not saved">
          {saveError}
        </Alert>
      )}

      <Card withBorder radius="md" padding="md" style={{ position: 'sticky', bottom: 12, zIndex: 5 }}>
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="xs">
            {page && (
              <Button variant="subtle" color="red" leftSection={<IconTrash size={16} />} onClick={() => setConfirmDelete(true)} disabled={save.isPending}>
                Remove help
              </Button>
            )}
            <Text size="sm" c={dirty ? 'orange' : 'dimmed'}>
              {dirty ? 'Unsaved changes' : page ? 'Saved' : 'Not written yet'}
            </Text>
          </Group>
          <Group gap="xs">
            <Button variant="default" leftSection={<IconArrowBackUp size={16} />} onClick={() => setForm(baseline)} disabled={!dirty || save.isPending}>
              Discard
            </Button>
            <Button leftSection={<IconDeviceFloppy size={16} />} onClick={() => save.mutate()} loading={save.isPending} disabled={!canSave}>
              Save page
            </Button>
          </Group>
        </Group>
      </Card>

      <ConfirmDialog
        opened={confirmDelete}
        onClose={() => !remove.isPending && setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
        closeOnConfirm={false}
        loading={remove.isPending}
        type="danger"
        title="Remove the help for this page?"
        message="People see that help is not written yet. Its tours stop too. You can write it again later."
        confirmLabel="Remove help"
        cancelLabel="Keep it"
      />
    </Stack>
  );
}
